import React, { FC, useState, useEffect, SetStateAction, Dispatch } from 'react';
import { View, KeyboardAvoidingView, TouchableOpacity, useWindowDimensions, ActivityIndicator, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { appFonts, appColors, fontSize } from '../../themes/appTheme';
import { Theme, ThemeConsumer } from "@rneui/themed";
import { config } from '../../shared/environment';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getOneRoleLocation, invitationCheck, sendPosStaff } from '../../redux/slices/inviteStaffSlice';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ZenDivider, ZenDropdown, ZenIcon, ZenInput, ZenToast, ZenText, ZenButton, ZenLoader } from 'zen-core-elements';
import { Pressable } from 'react-native';
import { setRoleProps } from './roleModule';
import { ScrollView } from 'react-native-gesture-handler';

interface InviteUserProps {
    setOpenInviteUser: Dispatch<SetStateAction<boolean>>,
    roleInvite: setRoleProps,
}

const InviteUser: FC<InviteUserProps> = ({ setOpenInviteUser, roleInvite }) => {
    const [locationOpen, setLocationOpen] = useState<boolean>(false)
    const [locationList, setLocationList] = useState<{ label: string, value: number }[] | null>(null)
    const { width } = useWindowDimensions();
    const isFocused = useIsFocused()
    const [isLoading, setIsLoading] = useState(false)
    const [storeEmails, setStoreEmails] = useState<string[]>([])
    const [checkLoading, setCheckLoading] = useState(false)
    const dispatch = useAppDispatch()
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/;
    const { value: currentUser } = useAppSelector((state) => state?.common?.currentUser)

    const { value: getOneLocationData } = useAppSelector(state => state.inviteStaffRole.getOneLocationMapping)
    useEffect(() => {
        if (isFocused) {
            setIsLoading(true)
            getOneLocationMapping()
        }
    }, [isFocused])

    const getOneLocationMapping = async () => {
        if (getOneLocationData?.locations?.rows?.length) {
            let formatDropDown = [...getOneLocationData.locations.rows]
            if (formatDropDown) {
                let location: { label: string, value: number }[] = []
                location = formatDropDown.map((i) => ({ label: i?.locationName, value: i?.id }))
                setLocationList(location)
                setIsLoading(false)
                return
            }
        }
        await dispatch(getOneRoleLocation({ storeId: config.storeId, data: { isForUser: true } })).unwrap().then((res) => {
            if (res.locations.rows) {
                let formatDropDown = [...res.locations.rows]
                if (formatDropDown) {
                    let location: { label: string, value: number }[] = []
                    location = formatDropDown.map((i) => ({ label: i?.locationName, value: i?.id }))
                    setLocationList(location)
                    setIsLoading(false)
                }
            }
        }).catch((err) => {
            console.log("err in getOneLocationmapping", err);
        })

    }
    const validationSchema = Yup.object().shape({
        locationList: Yup.array().of(Yup.number())
            .min(1, 'Location is required'),
        emailList:
            Yup.string()
                .label('Email')
                .matches(
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    'Email must end with ".com" or similar valid domain',
                ),

    })

    const formik = useFormik(
        {
            initialValues: {
                roleName: roleInvite?.name!,
                locationList: [],
                emailList: ''
            },
            enableReinitialize: true,
            validationSchema: validationSchema,
            onSubmit: () => {
                sendEmail()
            }
        })


    const multipleTextLocation = (valueId: number[]) => {
        let format = locationList
        let formatedText: string[] = []
        let changeValueType = valueId.map((i) => String(i))
        if (format) {
            formatedText = []
            formatedText = format.filter((findLocation) => changeValueType.includes(String(findLocation.value)))
                .map((i) =>
                    i.label = i.label[0].toLocaleUpperCase() + i.label.slice(1, i.label.length)
                )
        }
        return String(formatedText)
    }

    const sendEmail = async () => {
        let finalEmails = [...storeEmails]

        // If there's a valid email in the input that hasn't been blur-processed yet, run invitationCheck first
        const pendingEmail = formik.values.emailList
        if (emailRegex.test(pendingEmail) && !finalEmails.includes(pendingEmail)) {
            setCheckLoading(true)
            await dispatch(invitationCheck({ storeId: config.storeId, data: { email: pendingEmail } })).unwrap().then((res) => {
                if (res?.emailExists) {
                    finalEmails = [...finalEmails, pendingEmail]
                    setStoreEmails(finalEmails)
                } else {
                    ZenToast({ message: "This email does not exist", type: 'info' })
                }
                formik.setFieldValue('emailList', '')
                setCheckLoading(false)
            }).catch((err) => {
                console.log("err in invitationCheck (on send)", err)
                setCheckLoading(false)
            })
        }

        if (!finalEmails.length) return

        const data = {
            clientId: currentUser?.clientId!,
            emails: finalEmails,
            locationId: formik.values.locationList,
            role: {
                id: roleInvite?.id!,
                role: roleInvite?.name!,
                description: roleInvite?.description?.length ? roleInvite?.description : null,
                code: roleInvite?.code!,
                isDeleted: false
            }
        }

        await dispatch(sendPosStaff({ storeId: config.storeId, data: data })).unwrap().then((res) => {
            if (res?.createInvitation) {
                ZenToast({ type: 'success', message: "Email Sent Successfully" })
                setOpenInviteUser(false)
            }
            else {
                ZenToast({ type: 'error', message: "something went wrong " })
            }
        })
            .catch((err) => {
                console.log("err in sendPosStaff ", err);
                ZenToast({ message: err?.message, type: "error" })
            })

    }

    const checkEmail = async (mail: string) => {
        if (emailRegex.test(mail)) {
            await dispatch(invitationCheck({ storeId: config.storeId, data: { email: mail } })).unwrap().then((res) => {
                if (res?.emailExists) {
                    setStoreEmails((prev) => {
                        let mailData = [...prev]
                        if (!mailData.includes(formik.values.emailList)) {
                            mailData.push(formik.values.emailList)
                        }
                        else {
                            ZenToast(
                                {
                                    message: "This email is already exist",
                                    type: 'info'
                                })
                        }
                        return mailData
                    })
                }
                else {
                    ZenToast({ message: "This email is already exist", type: 'info' })
                }
                formik.setFieldValue('emailList', '')
                setCheckLoading(false)
            })
                .catch((err) => {
                    console.log("err in invitationCheck", err);
                })
        }
    }

    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => (
                <KeyboardAvoidingView behavior='padding' style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>

                    <View style={{ backgroundColor: appColors.light, borderRadius: 10, overflow: 'hidden', width: width > 900 ? 600 : width > 600 ? 500 : 300, minHeight: 300, paddingBottom: locationOpen ? 15 : 0 }}>

                        <View style={{ flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 15, alignItems: 'center', backgroundColor: appColors.lightBackgroundColor, paddingVertical: 10, width: '100%', }}>
                            <ZenText
                                content='Invite Staff' bold size={'medium'} color={appColors.lightDark}
                                style={{}} />
                            <TouchableOpacity onPress={() => { setOpenInviteUser(false); setLocationList(null) }}>
                                <ZenIcon type='material-community' name='close-circle' size={25} />
                            </TouchableOpacity>
                        </View>

                        {isLoading ?
                            <ZenLoader lottie={require("../../assets/infinityLoader.json")} lottieType='Local' viewStyle={{ alignItems: 'center', flex: 1, justifyContent: 'center' }} /> :
                            roleInvite?.name ?
                                <ScrollView >
                                    <View style={{ alignSelf: "center", paddingHorizontal: 15 }}>
                                        <View style={{ paddingTop: 10, alignItems: 'center' }}>
                                            <ZenInput
                                                inputStyle={{ height: 40 }}
                                                disabled={true}
                                                label="Role *"
                                                type='outlined'
                                                inputContainerStyle={{ paddingHorizontal: 8 }}
                                                labelStyle={{
                                                    fontSize: fontSize.smallerFontSize, color: appColors.labelColor
                                                }}
                                                onChangeText={() => { }}
                                                value={formik.values.roleName}
                                            />
                                            <View style={{ marginBottom: 12, alignItems: 'center' }}>
                                                <ZenDropdown
                                                    searchable
                                                    searchPlaceholder='Search Location'
                                                    style={{ height: 40 }}
                                                    labelStyle={{ fontSize: fontSize?.smallerFontSize, color: appColors?.dark, fontFamily: appFonts?.medium, }}
                                                    listItemLabelStyle={{ fontSize: fontSize?.smallerFontSize }}
                                                    title='Location'
                                                    placeholder=''
                                                    titleStyle={{ fontSize: fontSize.smallerFontSize, color: appColors.labelColor, marginBottom: 8 }}
                                                    items={locationList ?? []}
                                                    onSelectItem={(val) => {
                                                        formik.setFieldValue("locationList", val.map((i) => i.value))
                                                    }}
                                                    open={locationOpen}
                                                    setOpen={() => { setLocationOpen(!locationOpen) }}
                                                    setValue={() => undefined}
                                                    value={formik.values.locationList}
                                                    multiple={true}
                                                    multipleText={multipleTextLocation(formik.values.locationList)}
                                                    maxHeight={160}
                                                    dropDownContainerStyle={{
                                                        borderColor: appColors?.lightGrey,
                                                        backgroundColor: appColors.light
                                                    }}
                                                    onOpen={() => formik.setFieldTouched('locationList', true)}
                                                />

                                            </View>
                                            {
                                                formik.touched.locationList && formik.errors.locationList ?
                                                    <ZenText size={fontSize.errorFontSize} content={String(formik.errors.locationList)} style={{ textAlign: "left", alignSelf: "flex-start" }} color={appColors.red} /> : null
                                            }

                                            <View style={{ marginTop: 10, zIndex: -1 }}>
                                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                                    <ZenInput
                                                        inputStyle={{ height: 40 }}
                                                        label="Email *"
                                                        keyboardType="email-address"
                                                        autoCapitalize="none"
                                                        disabled={checkLoading}
                                                        labelStyle={{
                                                            fontSize: fontSize.smallerFontSize, color: appColors.labelColor
                                                        }}
                                                        type='outlined'
                                                        onBlur={async () => {
                                                            formik.setFieldTouched('emailList', true)
                                                            if (emailRegex.test(formik.values.emailList)) {
                                                                setCheckLoading(true)
                                                                await checkEmail(formik.values.emailList)
                                                            }
                                                        }}
                                                        placeholder=""
                                                        onChangeText={(val) => {
                                                            formik.setFieldValue('emailList', val.trim())
                                                        }}
                                                        textContentType='emailAddress'
                                                        value={String(formik.values.emailList)}
                                                        errorMessage={formik.touched.emailList && formik.errors.emailList ? String(formik.errors.emailList) : ''}
                                                        errorStyle={[theme.addEditProductStyle.errorStyle, { paddingBottom: formik.touched.emailList && formik.errors.emailList ? 20 : 0 }]}
                                                    />
                                                </View>
                                                {
                                                    storeEmails.length ?
                                                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', }}>
                                                            {
                                                                storeEmails.map((mailvalue, key) => (
                                                                    <Pressable key={key} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 10, paddingVertical: 5, backgroundColor: appColors.lightPrimary, marginHorizontal: 5, marginVertical: 10, borderRadius: 10, alignItems: 'center' }}>
                                                                        <ZenText content={mailvalue} size={fontSize.smallFontSize} color={appColors.primary} bold />
                                                                        <ZenIcon
                                                                            name='close-circle'
                                                                            type='material-community'
                                                                            color={appColors.lightDark}
                                                                            style={{ marginHorizontal: 10 }}
                                                                            size={18}
                                                                            onPress={() => {
                                                                                setStoreEmails((prev) => {
                                                                                    let remove = [...prev]
                                                                                    remove = remove.filter((findValue) => findValue != mailvalue)
                                                                                    return remove
                                                                                })
                                                                            }
                                                                            }
                                                                        />
                                                                    </Pressable>
                                                                ))
                                                            }
                                                        </View> : null
                                                }

                                            </View>
                                        </View>

                                        <View>
                                            {
                                                width > 600 ?
                                                    <ZenDivider style={{ marginTop: 7 }} />
                                                    : undefined
                                            }
                                        </View>

                                        <View style={{ alignSelf: "center", paddingVertical: 10 }}>
                                            <ZenButton
                                                title="Send"
                                                onPress={() => formik.handleSubmit()}
                                                titleStyle={{ fontSize: theme.mediumFontSize, fontFamily: theme.bold }}
                                                buttonStyle={{ backgroundColor: theme?.primary, borderRadius: 10, paddingHorizontal: 15, }}
                                            />
                                        </View>

                                    </View>
                                </ScrollView>
                                :
                                <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                    <ZenText content={'role has been deleted'} bold style={{ textTransform: 'capitalize' }} color={appColors.lightDark} />
                                </View>}
                    </View>
                </KeyboardAvoidingView>
            )
            }
        </ThemeConsumer >
    )
}

export default InviteUser;